import { Component, Input, OnInit, ViewEncapsulation, computed, signal, inject, ChangeDetectionStrategy, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { switchMap } from "rxjs/operators";

import { LOREN_ROUTES } from "../../../../../../constants/loren-routes.constants";
import { LorenSidenavTourSteps } from "../../../../../../constants/loren-tour-steps.constants";
import { LOREN_USER_ROLES } from "../../../../../../services/loren-authentication-service/constants/loren-user-roles.constants";
import { LorenUserInfo } from "../../../../../../interfaces/loren-user-info.interface";

import { LorenAuthenticationService } from "../../../../../../services/loren-authentication-service/loren-authentication.service";
import { LorenUsersEndpointsService } from "../../../../../../services/loren-users-endpoints-service/loren-users-endpoints.service";
import { LorenNavService } from "../../services/loren-nav-service/loren-nav.service";
import { LorenSnackBarService } from "../../../../../../services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenDetailsOutletService } from "../../../../../../services/loren-details-outlet-service/loren-details-outlet.service";
import { LorenUtilsService } from "../../../../../../services/loren-utils-service/loren-utils.service";

@Component({
  selector: "loren-user-menu",
  templateUrl: "./loren-user-menu.component.html",
  styleUrls: ["./loren-user-menu.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenUserMenuComponent implements OnInit {
  private router = inject(Router);
  private lorenAuthenticationService = inject(LorenAuthenticationService);
  private snackBarService = inject(LorenSnackBarService);
  private lorenNavigationService = inject(LorenNavService);
  private usersEndpoints = inject(LorenUsersEndpointsService);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  detailsOutletService = inject(LorenDetailsOutletService);

  @Input() isHandset: boolean;

  readonly userSettingsRoute = LOREN_ROUTES.NOTIFICATIONS_SETTINGS;
  readonly adminRoute = LOREN_ROUTES.ADMIN;
  readonly appRoute = LOREN_ROUTES.DASHBOARD;
  readonly userRoles = LOREN_USER_ROLES;
  readonly userProfileAnchor = LorenSidenavTourSteps.find(
    (x) => x.anchorId === "user-profile"
  ).anchorId;
  readonly userProfileRoute = `${LOREN_ROUTES.USER}/${LOREN_ROUTES.PROFILE}`;

  userInfo = signal<LorenUserInfo>(null);
  userDisplayName = computed<string>(() =>
    LorenUtilsService.resolveUserDisplayName(this.userInfo())
  );
  inAdminApp$ = this.lorenNavigationService.inAdminApp$;

  ngOnInit() {
    this.loadUserInfo();
  }

  handleLogout() {
    this.detailsOutletService.resetDetailsOutlet();
    this.lorenAuthenticationService.logout();
    this.snackBarService.showTranslatedSuccessMessage(`user.logout_successful`);
    setTimeout(() => this.router.navigate([LOREN_ROUTES.LOGIN]), 100);
  }

  handleAdminClick() {
    this.detailsOutletService.resetDetailsOutlet();
    setTimeout(() => this.router.navigate([this.adminRoute]), 100);
  }

  // Re-fetch /users/me on every session change (login, impersonation) so the
  // displayed name always reflects the currently active user.
  private loadUserInfo() {
    this.lorenAuthenticationService
      .getUserInfoSubscription()
      .pipe(
        switchMap(() =>
          this.http.get<LorenUserInfo>(this.usersEndpoints.userInfo())
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((userInfo) => this.userInfo.set(userInfo));
  }
}
